import { ReactElement } from 'react'
import { useQuery } from '@apollo/client'
import { GetStaticPropsResult } from 'next'
import markdownToHtml from '@/utils/markdownToHtml'
import { RichTextDescription } from '@/components/patterns/RichTextDescription'
import {
  Activity,
  getActivities,
  getActivity,
  getRelatedActivities,
} from '@/src/activities'
import { Layout } from '@/components/Layout'
import { Image } from '@/components/elements/Image'
import GET_NUMBER_OF_REGISTRATIONS_FOR_ACTIVITY_QUERY from '../../../apollo/queries/getNumberOfRegistrationsForActivity.graphql'
import { ActivityButton } from '@/components/elements/Button/activity'

import { formatDateTime } from '@/utils/formatDate'
import { ActivityCard } from '@/components/patterns/ActivityCard'
import { getEmbedURL } from '@/utils/getYoutubeEmbedURL'
import { Container } from '@/components/utils'
import { Divider } from '@/components/elements/Divider'
import { PageMeta } from '@/src/pages'
import ClientOnly from '@/components/ClientOnly'
import {
  ClockIcon,
  CalendarIcon,
  FlagIcon,
  LocationIcon,
  MapIcon,
} from '@/components/elements/ActivityCardIcons'
import {
  RegistrationType,
  GetNumberOfRegistrationsForActivityQuery,
} from '@/src/graphql-server/types/types'
import {
  NewsletterSection,
  NewsletterSectionProps,
} from '@/components/patterns/NewsletterSection'
import homeData from '@/content/home.json'

interface ActivityPageProps {
  meta: PageMeta
  activity: Activity
  relatedActivities: Activity[]
  newsletter: NewsletterSectionProps
}

export async function getStaticPaths() {
  const activities = getActivities()

  const paths = activities.map((activity) => ({
    params: { id: activity.id },
  }))

  return { paths, fallback: false }
}

export async function getStaticProps({
  params,
}): Promise<GetStaticPropsResult<ActivityPageProps>> {
  const activity = getActivity(params.id)

  const relatedActivities = getRelatedActivities(activity)

  const { title } = activity
  activity.description = await markdownToHtml(activity.description || '')
  const description = `Description for ${title}`
  return {
    props: {
      meta: {
        title,
        description,
      },
      activity,
      relatedActivities,
      newsletter: homeData.newsletter,
    },
  }
}

export default function ActivityPage({
  activity,
  relatedActivities,
  newsletter,
  meta,
}): ReactElement {
  const learnerQuery = useQuery<GetNumberOfRegistrationsForActivityQuery>(
    GET_NUMBER_OF_REGISTRATIONS_FOR_ACTIVITY_QUERY,
    {
      variables: {
        activityId: activity.id,
        userRole: RegistrationType.Learner,
      },
    }
  )
  const volunteerQuery = useQuery<GetNumberOfRegistrationsForActivityQuery>(
    GET_NUMBER_OF_REGISTRATIONS_FOR_ACTIVITY_QUERY,
    {
      variables: {
        activityId: activity.id,
        userRole: RegistrationType.Volunteer,
      },
    }
  )

  return (
    <Layout meta={meta}>
      <div className="grid gap-x-4 grid-cols-1 mx-auto max-w-7xl lg:grid-cols-2 lg:auto-rows-fr xl:px-5">
        <div className="aspect-w-6 aspect-h-7 md:aspect-w-5 md:aspect-h-6">
          <Image
            src={activity.thumbnail}
            alt={activity.title}
            layout="fill"
            objectFit="cover"
          />
        </div>
        <div className="flex flex-col pt-10 px-5 font-body">
          <span className="font-bold uppercase">{activity.category}</span>
          <h2 className="mt-2 font-display text-6xl font-black leading-none">
            {activity.title}
          </h2>
          <div className="flex flex-row flex-wrap items-center mt-8 py-2 border-b border-navy-light-300">
            <div className="flex items-center mr-4">
              <MapIcon className="flex-shrink-0 mr-2 w-5 h-5" />
              <span className="font-semibold"> {activity.venue}</span>
            </div>
            <div className="flex items-center">
              <CalendarIcon className="mr-2 w-6 font-semibold" />
              <span className="font-semibold">
                {formatDateTime(activity.date)}
              </span>
            </div>
          </div>
          <div className="flex items-center py-2 border-b border-navy-light-300">
            <LocationIcon className="mr-2 w-6 font-semibold" />
            <div className="text-orange-500 font-semibold">
              {!activity.addressLink && activity.address}
              {activity.address && (
                <a target="_blank" href={activity.addressLink} rel="noreferrer">
                  {activity.address}
                </a>
              )}
            </div>
          </div>
          <div className="flex flex-row flex-wrap items-center mb-4 py-2">
            <div className="flex items-center mr-4">
              <ClockIcon className="mr-2 w-6 font-semibold" />
              <span className="font-semibold">{activity.duration}</span>
            </div>
            <div className="flex items-center">
              <FlagIcon className="mr-2 w-6 font-semibold" />
              <span className="font-semibold">Nivell de català</span>
              <span className="ml-4 px-4 py-1 text-navy-600 2xl:font-medium bg-navy-light-100 rounded-2xl">
                {activity.catalanLevel}
              </span>
            </div>
          </div>

          <RichTextDescription
            description={activity.description}
            className="mb-8 font-body"
          />

          <p className="mb-4 font-semibold">Inscriure’m com a</p>
          <div className="flex items-center">
            <ClientOnly>
              <ActivityButton
                activity={activity}
                href={`/activities/${activity.id}/learner`}
                labels={{
                  bookingLabel: 'Aprenent',
                  upcomingLabel: 'Aprenent: aviat disponible',
                  closedLabel: 'Aprenent: No disponible',
                  noTicketsLabel: 'Aprenent: Places esgotades',
                }}
                data={learnerQuery.data}
                loading={learnerQuery.loading}
                error={learnerQuery.error}
                userRole={RegistrationType.Learner}
                checkForSeats
              />
              <span className="mx-2" />
              <ActivityButton
                activity={activity}
                href={`/activities/${activity.id}/volunteer`}
                labels={{
                  bookingLabel: 'Voluntari',
                  upcomingLabel: 'Voluntari: aviat disponible',
                  closedLabel: 'Voluntari: No disponible',
                  noTicketsLabel: 'Voluntari: Places esgotades',
                }}
                data={volunteerQuery.data}
                loading={volunteerQuery.loading}
                error={volunteerQuery.error}
                userRole={RegistrationType.Volunteer}
                checkForSeats
              />
            </ClientOnly>
          </div>
        </div>
      </div>
      <div className="mb-4 sm:mb-10">
        <Container>
          {activity.video && (
            <div className="aspect-w-4 aspect-h-2 mb-7 mt-16 md:mt-24 md:mx-16">
              <iframe
                title="featured video"
                src={getEmbedURL(activity.video)}
              />
            </div>
          )}
          <div className="hidden md:block md:mx-16">
            <Divider />
          </div>
          <h2 className="my-10 text-center text-4xl">
            Activitats relacionades
          </h2>
          <div className="grid gap-x-4 gap-y-8 grid-cols-1 mb-1 auto-rows-fr sm:grid-cols-2 lg:grid-cols-3">
            {relatedActivities.map((relatedActivity) => (
              <ActivityCard
                key={`${relatedActivity.id}`}
                activity={relatedActivity}
              />
            ))}
          </div>
        </Container>
      </div>
      <NewsletterSection {...newsletter} />
    </Layout>
  )
}
